import type {
  GetLayoutGridQueryVariables,
  GetLocationGridQueryVariables,
  GetMediaGridQueryVariables,
  GetPostGridQueryVariables,
  GetProfileGridQueryVariables,
  GetServiceGridQueryVariables,
  GetTagGridQueryVariables,
} from "@package/ui-gql/index-data-access";
import type { ActionReducerMapBuilder, PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

export type SliceItemGridStateEntry<T> = {
  filtersOpen: boolean;
  selectedItemId?: string;
  variables: T;
};

export type AnyApiGridQueryStateValue =
  | SliceItemGridStateEntry<GetLayoutGridQueryVariables>
  | SliceItemGridStateEntry<GetLocationGridQueryVariables>
  | SliceItemGridStateEntry<GetMediaGridQueryVariables>
  | SliceItemGridStateEntry<GetPostGridQueryVariables>
  | SliceItemGridStateEntry<GetProfileGridQueryVariables>
  | SliceItemGridStateEntry<GetServiceGridQueryVariables>
  | SliceItemGridStateEntry<GetTagGridQueryVariables>;

type AnyApiGridQueryState = { [key in string]: AnyApiGridQueryStateValue };

export type CreateSliceItemGridProps<T = AnyApiGridQueryState> = {
  initialState: T;
  extraReducers?: (builder: ActionReducerMapBuilder<T>) => void;
};

export function createSliceItemGrid<T extends AnyApiGridQueryState>({
  initialState,
  extraReducers,
}: CreateSliceItemGridProps<T>) {
  return createSlice({
    name: "itemGrid",
    initialState,
    reducers: {
      resetItemGridOptions: (draft) => {
        Object.assign(draft, initialState);
      },
      setItemGridFilterOpen: (
        draft,
        action: PayloadAction<{ path: keyof T; open: boolean }>,
      ) => {
        draft[action.payload.path].filtersOpen = action.payload.open;
      },
      setItemGridFilter: (
        draft,
        action: PayloadAction<{
          path: keyof T;
          filter: AnyApiGridQueryStateValue["variables"]["filter"];
        }>,
      ) => {
        draft[action.payload.path].variables.filter = action.payload.filter;
      },
      setItemGridPage: (
        draft,
        action: PayloadAction<{ path: keyof T; page: number }>,
      ) => {
        draft[action.payload.path].variables.page = action.payload.page;
      },
      setItemGridPerPage: (draft, action) => {
        draft[action.payload.path].variables.perPage = action.payload.perPage;
      },
      setItemGridQuery: (draft, action) => {
        if (action.payload.query) {
          draft[action.payload.path].variables.filter = {
            ...draft[action.payload.path].variables.filter,
            q: action.payload.query,
          };
        } else {
          delete draft[action.payload.path].variables?.filter?.q;
        }
      },
      setItemGridWithDeleted: (draft, action) => {
        if (typeof action.payload.withDeleted === "boolean") {
          if (action.payload.allPaths) {
            for (const path in draft) {
              draft[path].variables.filter = {
                ...draft[action.payload.path].variables.filter,
                withDeleted: action.payload.withDeleted,
              };
            }
          } else {
            draft[action.payload.path].variables.filter = {
              ...draft[action.payload.path].variables.filter,
              withDeleted: action.payload.withDeleted,
            };
          }
        } else {
          delete draft[action.payload.path].variables?.filter?.withDeleted;
        }
      },
      setItemGridSortField: (draft, action) => {
        draft[action.payload.path].variables.sortField = action.payload.field;
      },
      setItemGridSortOrder: (draft, action) => {
        draft[action.payload.path].variables.sortOrder = action.payload.order;
      },
      setItemGridSelectedItemId: (draft, action) => {
        draft[action.payload.path].selectedItemId = action.payload.id;
      },
    },
    extraReducers,
  });
}
